import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, TransferState } from '@angular/core';

import { isBotUserAgent } from '../functions/bot-detection';
import { SERVER_BOT_VERDICT, SM_BOT_VERDICT_STATE_KEY } from '../injection-tokens/server-bot-verdict.token';

/**
 * Resolves whether the current request is from a bot, preferring the server-computed verdict
 * over browser-side user-agent inspection. Both derive from the User-Agent, but the server
 * verdict is computed once from the request header and cannot be altered after the page is
 * rendered, whereas the browser's navigator.userAgent is read at runtime and false-positives on
 * headless Chrome (Playwright/Cypress).
 *
 * On the server the verdict is injected per request; on the browser it is read from the
 * TransferState the server serialized into the rendered HTML. A missing key means the page was
 * served through a CSR-fallback path that never ran the SSR render, so the verdict is unknown
 * and we fall back to user-agent inspection — never treat an absent key as "not a bot".
 */
@Injectable({ providedIn: 'root' })
export class BotDetectionService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transferState = inject(TransferState);
  private readonly serverVerdict = inject(SERVER_BOT_VERDICT, { optional: true });

  isBot(): boolean {
    if (isPlatformServer(this.platformId)) {
      return this.serverVerdict ?? this.userAgentFallback();
    }

    // navigator.webdriver is a browser-only signal the server can't observe, so it must be
    // consulted even when a server verdict was transferred — otherwise a clean-UA automation
    // session (transferred verdict false) would be treated as a human and tracked.
    if (this.isWebDriver()) {
      return true;
    }

    return this.isCrawler();
  }

  /**
   * Bot verdict EXCLUDING the automation signal — true only for a real crawler or HTTP client.
   *
   * Feature-flag evaluation gates on this rather than {@link isBot} so Playwright/Selenium
   * sessions still resolve real flag values (smoke tests assert on flag-gated UI), while
   * analytics and exposure tracking stay gated on `isBot()` and never count automation.
   */
  isCrawler(): boolean {
    if (isPlatformServer(this.platformId)) {
      return this.serverVerdict ?? this.userAgentFallback();
    }

    const isTransferredBot = this.transferState.hasKey(SM_BOT_VERDICT_STATE_KEY)
      ? this.transferState.get(SM_BOT_VERDICT_STATE_KEY, false)
      : undefined;

    return isTransferredBot ?? this.userAgentFallback();
  }

  protected userAgentFallback(): boolean {
    return isBotUserAgent();
  }

  protected isWebDriver(): boolean {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  }
}
